import DOMPurify from 'dompurify';
import type MarkdownIt from 'markdown-it';
import type { GlobalConfig } from 'md-editor-v3';

/**
 * The rendering rules of support messages. A support message body is Markdown from an operator
 * or from the support agent. md-editor-v3 renders it with `html: true` and with plugins that read
 * more syntax than plain Markdown. This module is the authoritative safety layer for that HTML.
 * The output filter of pos-backend (`AgentOutputFilter`) is the second layer for agent text.
 *
 * md-editor-v3 has one global config. `supportMdEditorConfig` changes markdown-it only for the
 * editor ids that `supportEditorId()` makes, so the announcement editor keeps its own behavior.
 */

export type SupportMarkdownAuthor = 'operator' | 'agent';

export interface SupportMarkdownPolicy {
  author: SupportMarkdownAuthor;
  /** The origin of pos-admin. The agent can link to its help pages, and to its pages with `appLinks`. */
  adminOrigin: string;
  /** The origin of uploaded images. If it is null, no image shows. */
  mediaOrigin: string | null;
  /**
   * The agent can also link to a page of pos-admin (`appLinkPath()`). Only the chat answer
   * sets it. A support message keeps help links only, like `AgentOutputFilter` of pos-backend.
   */
  appLinks?: boolean;
  /** The text in place of a removed image, so the reader knows that an image existed. */
  imagePlaceholder?: string;
}

const EDITOR_ID_PREFIX = 'support-msg-';

// The URL parser of the browser drops leading and trailing spaces and control characters, and
// every tab and newline. A value with any of them is refused, so the check and the browser read
// the same text.
const BROWSER_DROPPED_RE = /^[\u0000-\u0020]|[\u0000-\u0020]$|[\t\n\r]/;

/**
 * Parses an absolute URL with "//" after its scheme, as `^https?://` in `scheme` allows. Without
 * "//", the browser resolves "https:/x" or "https:x" against the page URL when the page has the
 * same scheme, while `new URL()` without a base reads "x" as the host.
 */
function parseAbsoluteUrl(value: string, scheme: RegExp): URL | null {
  return scheme.test(value) && !BROWSER_DROPPED_RE.test(value) ? parseUrl(value) : null;
}

const HTTPS_RE = /^https:\/\//i;
const HTTP_OR_HTTPS_RE = /^https?:\/\//i;

function parseUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

/** A message without an author type comes from an operator. Any other value gets the strict agent rules. */
export const supportMarkdownAuthor = (authorType: string | null | undefined): SupportMarkdownAuthor =>
  (authorType ?? 'operator') === 'operator' ? 'operator' : 'agent';

/** The origin of the media URL, or null if the URL is empty or not https. */
export const supportMediaOrigin = (mediaUrl: string | null | undefined): string | null => {
  const url = mediaUrl ? parseAbsoluteUrl(mediaUrl.trim(), HTTPS_RE) : null;
  return url !== null && url.protocol === 'https:' ? url.origin : null;
};

/** md-editor-v3 has text for zh-CN and en-US. plugins/md-editor.client.ts adds ja-JP. */
export const supportPreviewLanguage = (locale: string): 'ja-JP' | 'en-US' => (locale === 'ja' ? 'ja-JP' : 'en-US');

export const supportEditorId = (messageId: number | string): string => `${EDITOR_ID_PREFIX}${messageId}`;

const isSupportEditor = (editorId: string): boolean => editorId.startsWith(EDITOR_ID_PREFIX);

// The "image" plugin makes the figure that the image zoom of md-editor-v3 uses. The "code" plugin
// makes the code block header with the copy button. The other plugins (admonition, sub, sup, task
// list, heading ids, KaTeX, and Mermaid) add syntax that support messages do not need, and each
// one is more surface for injected text.
const SUPPORT_PLUGIN_TYPES = new Set(['image', 'code']);

// markdown-it gives the first word of the fence info string as the language. md-editor-v3 puts it
// into its highlight template without escaping. Only a plain language name can reach the class.
const LANGUAGE_RE = /^[A-Za-z0-9_+-]{1,32}$/;

const configureSupportMarkdownIt = (md: MarkdownIt): void => {
  md.set({
    // Raw HTML shows as text. Operators write with the composer, which makes no raw HTML.
    html: false,
    // md-editor-v3 calls this before its own template. A non-empty result replaces the template.
    highlight: (code: string, language: string) => {
      const className = LANGUAGE_RE.test(language) ? ` class="language-${language}"` : '';
      return `<pre><code${className}><span class="md-editor-code-block">${md.utils.escapeHtml(code.replace(/^\n+|\n+$/g, ''))}</span></code></pre>`;
    },
  });
  // Text such as "evil.com" or "a@evil.com" does not become a link. A URL with a scheme still does.
  md.linkify.set({ fuzzyLink: false, fuzzyEmail: false });
};

export const supportMdEditorConfig: Pick<GlobalConfig, 'markdownItConfig' | 'markdownItPlugins'> = {
  markdownItConfig: (md, { editorId }) => {
    if (isSupportEditor(editorId)) configureSupportMarkdownIt(md);
  },
  markdownItPlugins: (plugins, { editorId }) =>
    isSupportEditor(editorId) ? plugins.filter(plugin => SUPPORT_PLUGIN_TYPES.has(plugin.type)) : plugins,
};

// ── Link and image rules ──────────────────────────────────────────────────────

// The URL parser reads "https:evil.com" as "https://evil.com". Only the plain form is allowed, so
// the link shows where it goes. A user or a password before the host hides the real host from the
// reader, as in the agent rule.
const isAllowedOperatorHref = (href: string): boolean => {
  const url = parseAbsoluteUrl(href, HTTP_OR_HTTPS_RE);
  return url !== null && url.username === '' && url.password === '';
};

// The same rule as `AgentOutputFilter::isAllowedUrl()` of pos-backend. The part after the origin
// holds only the help path and a percent-encoded heading id. Thus no second URL, query, or
// protocol-relative path can start in it.
const HELP_URL_REST_RE = /^\/help\/(?:[A-Za-z0-9\-._/#]|%[0-9A-Fa-f]{2})+$/;
const UNSAFE_HELP_PATH_RE = /\/\.\.?(\/|$)|%2e|%2f|%5c/i;

/** A link of agent text must point to a help page of pos-admin. */
export const isAllowedAgentHref = (href: string, adminOrigin: string): boolean => {
  const origin = parseUrl(adminOrigin)?.origin;
  // The URL parser gives the scheme and the host in lower case. The path rule stays exact.
  if (!origin || origin === 'null' || href.slice(0, origin.length).toLowerCase() !== origin) return false;

  const rest = href.slice(origin.length);
  if (!HELP_URL_REST_RE.test(rest) || rest.includes('//')) return false;

  // The origin prefix already holds "//", and the rest has no space, tab, or newline.
  const url = parseAbsoluteUrl(href, HTTP_OR_HTTPS_RE);
  return (
    url !== null &&
    url.origin === origin &&
    url.username === '' &&
    url.password === '' &&
    url.search === '' &&
    url.pathname.startsWith('/help/') &&
    !UNSAFE_HELP_PATH_RE.test(rest.split('#')[0])
  );
};

// The same rule as `isAllowedAppUrl()` of pos-agent: lowercase path segments and an optional query
// of plain parameters, for example "/menu-items" or "/report-menu-items?tab=engineering". The help
// docs link to these pages, and the chat opens them in the app.
const APP_URL_REST_RE =
  /^(?:\/[a-z0-9][a-z0-9-]*)+(?:\?[A-Za-z0-9_.-]+=[A-Za-z0-9_.-]*(?:&[A-Za-z0-9_.-]+=[A-Za-z0-9_.-]*)*)?$/;
// The API, the help pages, the static files of the docs, and the build files are not app pages.
// The sign-in, invite, password reset, OAuth callback, and short link pages run a token or an
// account flow, so a link from agent text must not open them.
const NON_PAGE_SEGMENTS = new Set([
  'api',
  'help',
  'docs',
  '_nuxt',
  'a',
  'invite',
  'password-reset',
  'signin',
  'square-oauth',
]);

/** A link of chat text can point to a page of pos-admin. The result is the path, or null. */
export const appLinkPath = (href: string, adminOrigin: string): string | null => {
  const origin = parseUrl(adminOrigin)?.origin;
  if (!origin || origin === 'null' || href.slice(0, origin.length).toLowerCase() !== origin) return null;
  const rest = href.slice(origin.length);
  if (!APP_URL_REST_RE.test(rest) || NON_PAGE_SEGMENTS.has(rest.split(/[/?]/)[1] ?? '')) return null;
  const url = parseAbsoluteUrl(href, HTTP_OR_HTTPS_RE);
  return url !== null && url.origin === origin && url.username === '' && url.password === '' ? rest : null;
};

/** Operators attach images through the media upload of pos-backend. Other images do not load. */
export const isAllowedSupportImage = (src: string, mediaOrigin: string | null): boolean => {
  const media = mediaOrigin ? parseAbsoluteUrl(mediaOrigin, HTTPS_RE) : null;
  const url = parseAbsoluteUrl(src, HTTPS_RE);
  return media !== null && url !== null && media.protocol === 'https:' && url.origin === media.origin;
};

const isAppLink = (href: string, policy: SupportMarkdownPolicy): boolean =>
  policy.author === 'agent' && policy.appLinks === true && appLinkPath(href, policy.adminOrigin) !== null;

const isAllowedHref = (href: string, policy: SupportMarkdownPolicy): boolean =>
  policy.author === 'agent'
    ? isAllowedAgentHref(href, policy.adminOrigin) || isAppLink(href, policy)
    : isAllowedOperatorHref(href);

const isAllowedImage = (src: string, policy: SupportMarkdownPolicy): boolean =>
  policy.author === 'operator' && isAllowedSupportImage(src, policy.mediaOrigin);

// ── Sanitizer ─────────────────────────────────────────────────────────────────

const TEXT_TAGS = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'ul',
  'ol',
  'li',
  'code',
  'pre',
  'blockquote',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'strong',
  'em',
  'del',
  's',
  'a',
  'br',
  'hr',
  // The code block of md-editor-v3: its header, its copy button, and its code span.
  'span',
  'div',
  'details',
  'summary',
];
const IMAGE_TAGS = ['img', 'figure', 'figcaption'];

// No style, no id, and no data attributes. The hook below adds rel and target to links.
const ALLOWED_ATTR = ['href', 'title', 'src', 'alt', 'class', 'start', 'open'];

// ALLOWED_ATTR is one list for all tags in DOMPurify. The hook keeps each attribute only on these
// tags. Any tag can hold a class.
const TAG_ATTRS: Record<string, readonly string[]> = {
  A: ['href', 'title'],
  IMG: ['src', 'alt', 'title'],
  OL: ['start'],
  DETAILS: ['open'],
};

// A class can only refer to existing CSS. These are the classes that the code and image plugins of
// md-editor-v3 5.8.5 write. Other names, for example "md-editor-fullscreen" or the Vuetify utility
// classes, can lay a message over the page.
const ALLOWED_CLASSES = new Set([
  'md-editor-code',
  'md-editor-code-head',
  'md-editor-code-flag',
  'md-editor-code-action',
  'md-editor-code-lang',
  'md-editor-copy-button',
  'md-editor-collapse-tips',
  'md-editor-code-block',
  'md-editor-codetab-label',
  'md-editor-codetab-lang',
  'md-zoom',
]);
const LANGUAGE_CLASS_RE = /^language-[A-Za-z0-9_+-]{1,32}$/;
const isAllowedClass = (name: string): boolean => ALLOWED_CLASSES.has(name) || LANGUAGE_CLASS_RE.test(name);

const LINK_REL = 'noopener noreferrer nofollow';

let purifier: ReturnType<typeof DOMPurify> | null = null;
// The DOMPurify hook reads the policy of the call in progress. sanitize() is synchronous, so no
// other call can change it during the run.
let activePolicy: SupportMarkdownPolicy | null = null;

const getPurifier = (): ReturnType<typeof DOMPurify> => {
  if (purifier) return purifier;

  // A separate instance keeps these hooks away from the other DOMPurify callers of the app.
  purifier = DOMPurify(window);
  purifier.addHook('afterSanitizeAttributes', node => {
    const policy = activePolicy;
    if (!policy || !(node instanceof Element)) return;

    const tagAttrs = TAG_ATTRS[node.tagName] ?? [];
    for (const name of node.getAttributeNames()) {
      if (name !== 'class' && !tagAttrs.includes(name)) node.removeAttribute(name);
    }

    const classes = (node.getAttribute('class') ?? '').split(/\s+/).filter(isAllowedClass);
    if (classes.length) node.setAttribute('class', classes.join(' '));
    else node.removeAttribute('class');

    // DOMPurify keeps the content of these elements. applyPolicy() then unwraps a link without
    // href and replaces an image without src.
    if (node.tagName === 'A') {
      const href = node.getAttribute('href');
      if (href === null || !isAllowedHref(href, policy)) {
        node.removeAttribute('href');
        return;
      }
      node.setAttribute('rel', LINK_REL);
      // An app link opens in the same tab. ChatAnswerBody.vue opens it with the router.
      if (!isAppLink(href, policy)) node.setAttribute('target', '_blank');
    }
    if (node.tagName === 'IMG' && !isAllowedImage(node.getAttribute('src') ?? '', policy)) {
      node.removeAttribute('src');
    }
  });
  return purifier;
};

/**
 * Removes the links and images that the policy refuses. A removed link keeps its text, and a
 * removed image leaves the placeholder. It runs on the output of DOMPurify, which holds only the
 * allowed tags, so the parse and the serialization give the same tree. The document of DOMParser
 * is inert: it runs no script and loads no image.
 */
const applyPolicy = (html: string, policy: SupportMarkdownPolicy): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  for (const link of Array.from(doc.body.querySelectorAll('a'))) {
    const href = link.getAttribute('href');
    if (href === null || !isAllowedHref(href, policy)) link.replaceWith(...Array.from(link.childNodes));
  }
  for (const image of Array.from(doc.body.querySelectorAll('img'))) {
    if (!isAllowedImage(image.getAttribute('src') ?? '', policy)) {
      image.replaceWith(policy.imagePlaceholder ?? '');
    }
  }
  // The figure of a removed image keeps the placeholder but not the caption.
  for (const figure of Array.from(doc.body.querySelectorAll('figure'))) {
    if (figure.querySelector('img')) continue;
    figure.querySelector('figcaption')?.remove();
    figure.replaceWith(...Array.from(figure.childNodes));
  }
  // Agent output holds no image tags at all, also no stray caption.
  if (policy.author === 'agent') {
    for (const element of Array.from(doc.body.querySelectorAll('figure, figcaption'))) {
      element.replaceWith(...Array.from(element.childNodes));
    }
  }
  return doc.body.innerHTML;
};

export const sanitizeSupportHtml = (html: string, policy: SupportMarkdownPolicy): string => {
  const instance = getPurifier();
  activePolicy = policy;
  try {
    const clean = instance.sanitize(html, {
      // applyPolicy() replaces each refused image, also every image of the agent, with the placeholder.
      ALLOWED_TAGS: [...TEXT_TAGS, ...IMAGE_TAGS],
      ALLOWED_ATTR,
      ALLOW_DATA_ATTR: false,
      ALLOW_ARIA_ATTR: false,
      ALLOWED_URI_REGEXP: /^https?:/i,
      // DOMPurify tests each attribute that is not URI safe against ALLOWED_URI_REGEXP, so it
      // drops a list start such as "3" without this.
      ADD_URI_SAFE_ATTR: ['start'],
    });
    return applyPolicy(clean, policy);
  } finally {
    activePolicy = null;
  }
};

/** The `sanitize` function of MdPreview for a support message. */
export const supportSanitizer =
  (policy: SupportMarkdownPolicy) =>
  (html: string): string =>
    sanitizeSupportHtml(html, policy);
